import { ReactNode } from "react";
import SntImageView from "@widgets/images/SntImageView";
import { DefaultTag, StateColorTag } from "@cleanup/components/TagMore";
import { stateColorOf, stateIconMask } from "@/utils/stateColorUtils";
import { useStateIconKind } from "@/utils/stateIconKind";

interface StateTagProps {
  /** the state's icon; none: the label alone */
  src?: string | null;
  label: ReactNode;
  /** STIC-16316 R5: the state's color of the palette; none: the platform's white tag */
  color?: string | null;
  /** the tooltip; by default the label, when it is text */
  title?: string;
  size?: "sm" | "md";
  className?: string;
}

/**
 * A state as a tag: its label in the font color of its color on that color's
 * background, its icon in the color, as the Preview of the state editors shows
 * it; a state without a color in the platform's white tag. The same wherever a
 * state is shown (.snt-state-tag: 28px high, text 13px weight 600).
 */
const StateTag = ({
  src,
  label,
  color,
  title,
  size = "md",
  className = "",
}: StateTagProps) => {
  const known = stateColorOf(color);
  // in a tag with a color, an SVG icon takes its color and a PNG/JPG sits on a white circle
  const kind = useStateIconKind(known ? src : null);
  // a state with neither a label nor an icon shows nothing, not an empty box
  const noLabel =
    label == null ||
    label === false ||
    (typeof label === "string" && !label.trim());
  if (noLabel && !src) return null;
  const tooltip = title ?? (typeof label === "string" ? label : undefined);
  const content = (
    <>
      {src &&
        (!known ? (
          <SntImageView src={src} width="18px" height="18px" />
        ) : kind === "svg" ? (
          // R7: the SVG drawn in the state's color
          <span
            aria-hidden="true"
            className="snt-state-glyph-tint"
            style={stateIconMask(src)}
          />
        ) : (
          // a PNG/JPG keeps its own colors on a white circle (also while the kind is not known yet)
          <span className="snt-state-glyph-disc">
            <SntImageView src={src} width="14px" height="14px" />
          </span>
        ))}
      <span className="text-truncate" style={{ minWidth: 0 }}>
        {label}
      </span>
    </>
  );
  return known ? (
    <StateColorTag
      color={known}
      title={tooltip}
      size={size}
      className={`snt-state-tag ${className}`}
    >
      {content}
    </StateColorTag>
  ) : (
    <DefaultTag
      title={tooltip}
      size={size}
      className={`snt-state-tag ${className}`}
    >
      {content}
    </DefaultTag>
  );
};

export default StateTag;
